import { Button } from '@artic/ui/button';
import { Input } from '@artic/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@artic/ui/select';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { useQueryStates } from 'nuqs';
import { useCallback } from 'react';

import { usePartnerName } from '../../features/inbox/use-partner-name';
import { visionTestPlansListQueryOptions } from '../../features/vision-test-plans/queries';
import type { PlanSortColumn } from '../../features/vision-test-plans/search';
import {
  PLANS_DEFAULT_LIMIT,
  visionTestPlanListSearchParsers,
} from '../../features/vision-test-plans/search';

export const Route = createFileRoute('/vision_test_plans/')({
  staticData: {
    breadcrumb: 'Vision Test Plans',
    layout: 'default',
  },
  component: VisionTestPlansListPage,
});

function outcomeLabel(allPass: boolean, allFail: boolean): string {
  if (allPass) return 'Should all pass';
  if (allFail) return 'Should all fail';
  return 'Manually check';
}

function SortIndicator({
  col,
  sortBy,
  sortOrder,
}: {
  col: PlanSortColumn;
  sortBy: PlanSortColumn;
  sortOrder: 'asc' | 'desc';
}) {
  if (sortBy !== col) return <span className="ml-1 opacity-30">↕</span>;
  return <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
}

function VisionTestPlansListPage() {
  const partnerName = usePartnerName();
  const [{ page, name, expectedOutcome, sortBy, sortOrder }, setSearch] = useQueryStates(
    visionTestPlanListSearchParsers,
  );
  const safePage = page >= 1 ? page : 1;

  const { data, isLoading, isFetching } = useQuery(
    visionTestPlansListQueryOptions(partnerName, {
      page: safePage,
      limit: PLANS_DEFAULT_LIMIT,
      name: name || undefined,
      expectedOutcome: expectedOutcome || undefined,
      sortBy,
      sortOrder,
    }),
  );

  const plans = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / PLANS_DEFAULT_LIMIT) : 1;
  const hasPrev = safePage > 1;
  const hasNext = safePage < totalPages;

  const handleSort = useCallback(
    (col: PlanSortColumn) => {
      void setSearch((prev) => ({
        page: 1,
        sortBy: col,
        sortOrder: prev.sortBy === col && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      }));
    },
    [setSearch],
  );

  const hasFilters = !!name || !!expectedOutcome;

  return (
    <div className="flex flex-col gap-4 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Vision Test Plans</h1>
        <p className="text-muted-foreground text-sm">
          Prompt-lab for vision quality AI evaluation. Select evidence images, create a plan, and
          compare runs with different prompt configurations.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Filter by name…"
          value={name}
          onChange={(e) => void setSearch({ name: e.target.value, page: 1 })}
          className="h-8 w-56 text-sm"
        />
        <Select
          value={expectedOutcome || '__all__'}
          onValueChange={(v) =>
            void setSearch({
              expectedOutcome: (v === '__all__' ? '' : v) as typeof expectedOutcome,
              page: 1,
            })
          }
        >
          <SelectTrigger className="h-8 w-44 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            <SelectItem value="all_pass">Should all pass</SelectItem>
            <SelectItem value="all_fail">Should all fail</SelectItem>
            <SelectItem value="manual">Manually check</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => void setSearch({ name: '', expectedOutcome: '', page: 1 })}
          >
            Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : plans.length === 0 && safePage === 1 && !hasFilters ? (
        <div className="border-border rounded border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm">
            No test plans yet. Create one from the{' '}
            <a
              href="/quality/vision-quality-checks"
              className="text-foreground underline underline-offset-2"
            >
              Vision Quality Checks
            </a>{' '}
            page by filtering evidence and clicking <strong>New Vision Test Plan</strong>.
          </p>
        </div>
      ) : (
        <>
          <div className="border-border overflow-hidden rounded border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-muted-foreground text-left">
                <tr>
                  <th
                    className="hover:text-foreground cursor-pointer px-4 py-2 font-medium select-none"
                    onClick={() => handleSort('name')}
                  >
                    Name
                    <SortIndicator col="name" sortBy={sortBy} sortOrder={sortOrder} />
                  </th>
                  <th className="px-4 py-2 font-medium">Filters</th>
                  <th
                    className="hover:text-foreground cursor-pointer px-4 py-2 font-medium select-none"
                    onClick={() => handleSort('type')}
                  >
                    Type
                    <SortIndicator col="type" sortBy={sortBy} sortOrder={sortOrder} />
                  </th>
                  <th
                    className="hover:text-foreground cursor-pointer px-4 py-2 text-right font-medium select-none"
                    onClick={() => handleSort('total_items')}
                  >
                    Items
                    <SortIndicator col="total_items" sortBy={sortBy} sortOrder={sortOrder} />
                  </th>
                  <th
                    className="hover:text-foreground cursor-pointer px-4 py-2 font-medium select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    Created
                    <SortIndicator col="created_at" sortBy={sortBy} sortOrder={sortOrder} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted-foreground px-4 py-8 text-center text-sm">
                      No plans match the current filters.
                    </td>
                  </tr>
                ) : (
                  plans.map((plan) => {
                    const filters = [
                      plan.filter_operation_name,
                      plan.filter_check_name,
                      plan.filter_acceptance_criteria,
                    ]
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <tr key={plan.test_plan_id} className="border-border border-t">
                        <td className="px-4 py-2">
                          <Link
                            to="/vision_test_plans/$test_plan_id"
                            params={{ test_plan_id: plan.test_plan_id }}
                            className="text-foreground font-medium hover:underline"
                          >
                            {plan.name}
                          </Link>
                          {plan.notes && (
                            <p className="text-muted-foreground mt-0.5 max-w-xs truncate text-xs">
                              {plan.notes}
                            </p>
                          )}
                        </td>
                        <td className="text-muted-foreground px-4 py-2 text-xs">
                          {filters || <span className="opacity-40">—</span>}
                        </td>
                        <td className="text-muted-foreground px-4 py-2 text-xs">
                          {outcomeLabel(
                            plan.expected_outcome_all_pass,
                            plan.expected_outcome_all_fail,
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{plan.total_items}</td>
                        <td className="text-muted-foreground px-4 py-2 text-xs">
                          {formatDistanceToNow(new Date(plan.created_at), { addSuffix: true })}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Page {safePage} of {totalPages} · {total} plans
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrev || isFetching}
                  onClick={() => void setSearch({ page: safePage - 1 })}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNext || isFetching}
                  onClick={() => void setSearch({ page: safePage + 1 })}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
